import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { Icon } from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import { Link } from '@delmonicos/shared-design-system-react/src/components/link/link';
import { withProtectedResource } from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Tag } from '@delmonicos/shared-design-system-react/src/components/tag/tag';
import {
  Text,
  TextColor,
  TextFontSize,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { HeadingEnum } from '@delmonicos/shared-design-system-react/src/models/enums/heading.enum';
import { GetChargePointsInMaintenancePaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/inputs/get-charge-points-in-maintenance-paginated.model';
import { GatewayChargePointInMaintenanceModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/outputs/gateway-charge-point-in-maintenance.model';
import { GatewayChargePointsInMaintenancePaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/outputs/gateway-charge-points-in-maintenance-paginated.model';
import { safeHasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { transformPaginatedResponse } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/transform-api-paginated-response/transform-api-response';
import { TransformedPaginatedData } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/transform-api-paginated-response/transformed-paginated-data.model';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import { SortingState } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createSearchParams,
  generatePath,
  useNavigate,
} from 'react-router-dom';

import { APP_PATH } from '@app/config/app-paths';
import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import {
  CHARGE_POINT_LIST_SORT_FIELD,
  CHARGE_POINT_LIST_SORT_ORDER,
} from '@app/feature/charge-point/charge-point-list/charge-point-list.config';
import { useCpmsBoGetChargePointsInMaintenanceQuery } from '@service/cpms-bo-charge-point.service';

import { ChargePointsComponent } from './charge-points/charge-points.component';

interface MaintenanceCardViewProps {
  resource: TransformedPaginatedData<GatewayChargePointInMaintenanceModel>;
}

export const MaintenanceCardView = ({
  resource,
}: MaintenanceCardViewProps): React.JSX.Element => {
  return <ChargePointsComponent chargePoints={resource} />;
};

export const MaintenanceCardComponent = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const navigate = useNavigate();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  // PAGINATION ////////////////////////////////////////////////////////////////

  const [paginationLimit /* , setPaginationLimit */] = useState(5);
  const [paginationOffset /* , setPaginationOffset */] = useState<number>(0);
  const [paginationSorting /* , setPaginationSorting */] =
    useState<SortingState>([
      {
        desc: CHARGE_POINT_LIST_SORT_ORDER === 1,
        id: CHARGE_POINT_LIST_SORT_FIELD,
      },
    ]);

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const getPaginatedQueryParameters = useMemo(() => {
    const params: GetChargePointsInMaintenancePaginatedModel = {
      limit: paginationLimit,
      offset: paginationOffset,
    };

    const paginationSorting0 = paginationSorting.at(0);

    if (paginationSorting0) {
      params.sortField = paginationSorting0.id as any;
      params.sortOrder = paginationSorting0.desc ? -1 : 1;
    }

    return params;
  }, [paginationLimit, paginationOffset, paginationSorting]);

  const queryState = useCpmsBoGetChargePointsInMaintenanceQuery(
    getPaginatedQueryParameters,
  );

  // RENDER ////////////////////////////////////////////////////////////////////

  const { Component: MaintenanceCardViewPropsProtected } =
    withProtectedResource<
      TransformedPaginatedData<GatewayChargePointInMaintenanceModel>,
      {},
      GatewayChargePointsInMaintenancePaginatedModel
    >(MaintenanceCardView, queryState, {
      isAuthorized: () =>
        safeHasAccessTo(userPermissions, {
          resourceName: Resource.CHARGE_POINTS,
          action: ActionsByResource.CHARGE_POINTS.READ,
        }),
      transformDataToResource: transformPaginatedResponse,
    })({});

  return (
    <Card flex={1} gap={GapEnum.L} justifyContent="space-between">
      <Stack gap={GapEnum.L} direction="column">
        <Stack gap={GapEnum.XXL} justifyContent="space-between">
          <Stack flex={1}>
            <AccentTitle
              as={HeadingEnum.H3}
              variant={AccentVariantEnum.Accent1}
              icon="electric-terminal"
              text={t('$.dashboard.maintenance.title')}
            />
          </Stack>
          <Tag className="bg-surface-background" flex={1}>
            <Stack gap={GapEnum.S} alignItems="center">
              <Text
                bold
                fontSize={TextFontSize.Large}
                color={TextColor.Primary}>
                {queryState.data?.totalDocs ?? '-'}
              </Text>
              <Text color={TextColor.Secondary}>
                {t('$.dashboard.maintenance.connectors.total')}
              </Text>
            </Stack>
          </Tag>
        </Stack>
        {MaintenanceCardViewPropsProtected}
      </Stack>
      <Stack justifyContent="center" gap={GapEnum.S}>
        <Link
          onClick={() =>
            navigate({
              pathname: generatePath(APP_PATH.CHARGE_POINT.LIST),
              search: createSearchParams({
                isMaintenance: 'true',
              }).toString(),
            })
          }>
          {t('$.dashboard.maintenance.charge-points.see-more.title')}
        </Link>
        <Icon name="chevron-right" />
      </Stack>
    </Card>
  );
};
